import { Alert } from "@mui/material";
import MuiAlert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Dialog from "@mui/material/Dialog";
import Divider from "@mui/material/Divider";
import Snackbar from "@mui/material/Snackbar";
import { ConfirmProvider } from "material-ui-confirm";
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";

import { ShiftDetailsDialog } from "../../../../export/scheduler";
import { resetRenotifyWorkers } from "../../../../export/scheduler";
import { NewShiftDialog } from "../../../../export/shift-scheduler";
import AppliedFilters from "../../../components/filters/applied-filters";
import useWorkerSchedulerData from "../../../components/full-time-worker-scheduler/controllers/use-worker-scheduler-data";
import {
  resetUpdateWorkerAvailabilityStatus,
  updateWorkerAvailabilityStatus,
} from "../../../components/full-time-worker-scheduler/store/worker-scheduler-slice";
import CallOffSection from "../../../components/full-time-worker-scheduler/ui/dialogs/call-off-dialog/call-off-section"
import { defaultConfirmOptions } from "../../../config/confirm-config";
import useColumnSelector from "../../../design/column-selector/controllers/use-column-selector";
import useWorkerRequestsData from "../controllers/use-worker-requests-data";
import { resetShiftActions } from "../store/worker-requests-slice";
import { RegularShiftRequest } from "../store/worker-requests-types";
import useColumnDivider from "./controllers/use-column-divider";
import useFullTimeRequestsColumns from "./controllers/use-full-time-request-columns";
import useFullTimeRequestController from "./controllers/use-full-time-request-controller";
import FullTimeRequestsTable from "./table/full-time-requests-table";
import CallDetailDrawer from "./ui/call-detail-drawer";
import NewWorkerRequestDialog from "./ui/new-worker-request-dialog";
import WorkerRequestDetail from "./ui/worker-request-detail";
import WorkerRequestHeader from "./ui/worker-request-header";

const FullTimeShiftsSwap: React.FC = () => {
	const {
		requestActionStatus,
		requestActionErrorMessage,
		renotifyWorkersStatus,
	} = useWorkerRequestsData();
	const dispatch = useDispatch();
	const navigate = useNavigate();
	const { t } = useTranslation();

	const handleClose = () => {
		dispatch(resetShiftActions());
		if (requestActionStatus !== "error") {
			navigate("/login");
		}
	};

	const [
		{
			isCreateNewShiftDialogOpen,
			isShiftDialogOpen,
			draftShiftDate,
			searchQuery,
			isShiftCoverage,
			rows,
			requestStatus,
			timeFilterOption,
			shiftDetailsId,
      selectedRequestToEdit,
      isEditCallOffOpen,
			isRaiseRequestDialogOpen,
			useSharedFilters,
			dateRange,
		},
		{
			openCreateNewShiftDialog,
			setIsShiftDialogOpen,
			closeCreateNewShiftDialog,
			setSearchQuery,
			handleShiftCoverage,
			setRequestStatus,
			setTimeFilterOption,
			setDateRange,
			openShifDetailsDialog,
			closeShiftDetailsDialog,
      closeEditDialog,
      handleEditClick,
      fetchRequestsWithFilter,
			openRaiseRequestDialog,
			closeRaiseRequestDialog,
		},
	] = useFullTimeRequestController();
  const {
    updateAvailabilityStatusStatus,
    updateAvailabilityStatusErrorMessage,
  } = useWorkerSchedulerData();
  const [callDetailRequest, setCallDetailRequest] =
    useState<RegularShiftRequest | null>(null);

  useEffect(() => {
    if (updateAvailabilityStatusStatus === "fulfilled") {
      if (closeEditDialog) {
        closeEditDialog();
      }
    }
  }, [updateAvailabilityStatusStatus, closeEditDialog]);

	const columns = useFullTimeRequestsColumns(
		searchQuery ?? "",
		handleEditClick,
		setCallDetailRequest
	);

	const fixedColumns = ["workerName", "actions"];
	const showByDefault = [
    "workCenterName",
		"requestType",
		"shiftDetails",
    "leaveType",
		"comment",
		"status",
		"requestDate",
		"actionTakenBy",
	];
	const { defaultColumns, restColumns } = useColumnDivider(
		columns,
		fixedColumns,
		showByDefault
	);
	//	console.log("defaultColumns", defaultColumns, "restColumns", restColumns);
	const [{ selectedColumns, pickerColumns }, { handleSelectColumns }] =
		useColumnSelector(defaultColumns, restColumns);

	return (
					<ConfirmProvider defaultOptions={defaultConfirmOptions}>
      <Box>
        <WorkerRequestHeader
          search={setSearchQuery}
          value={searchQuery ?? ""}
          columns={pickerColumns.filter(
            (column: { field: string }) => column.field !== "actions"
          )}
          handleSelectColumns={handleSelectColumns}
          statusOptions={requestStatus}
          setStatusOptions={setRequestStatus}
          timeFilterOption={timeFilterOption}
          setTimeFilterOption={setTimeFilterOption}
          onRaiseRequestClick={openRaiseRequestDialog}
          useSharedFilters={useSharedFilters}
          dateRange={dateRange}
          setDateRange={setDateRange}
        />
        {useSharedFilters && <Divider />}
        {useSharedFilters && <AppliedFilters />}
        <NewWorkerRequestDialog
          open={isRaiseRequestDialogOpen}
          onClose={closeRaiseRequestDialog}
          onSuccess={() => fetchRequestsWithFilter()}
        />
        <FullTimeRequestsTable
          openCreateNewShiftDialog={openCreateNewShiftDialog}
          openShiftDetailsDialog={openShifDetailsDialog}
          columns={columns.filter((column: any) =>
            selectedColumns?.includes(column.field)
          )}
          rows={rows}
        />
        {isCreateNewShiftDialogOpen ? (
          <NewShiftDialog
            open={isCreateNewShiftDialogOpen}
            handleClose={closeCreateNewShiftDialog}
            draftShiftDate={
              draftShiftDate ? new Date(draftShiftDate) : new Date()
            }
            isShiftSwap={true}
            requestComponent={
              <WorkerRequestDetail
                handleShiftCoverage={handleShiftCoverage}
                isShiftCoverage={isShiftCoverage}
              />
            }
            isShiftCoverage={isShiftCoverage}
          />
        ) : null}
        {isShiftDialogOpen ? (
          <ShiftDetailsDialog
            open={isShiftDialogOpen}
            handleClose={closeShiftDetailsDialog}
            shiftId={shiftDetailsId}
            isWorkerRequest={true}
          />
        ) : null}

        {requestActionStatus === "fulfilled" ? (
          <Snackbar
            open={requestActionStatus === "fulfilled"}
            autoHideDuration={3000}
            onClose={() => dispatch(resetShiftActions())}
            anchorOrigin={{ vertical: "top", horizontal: "center" }}
          >
            <MuiAlert elevation={6} variant="filled" severity="success">
              {t("worker-requests_shift-status-updated")}
            </MuiAlert>
          </Snackbar>
        ) : null}

        {requestActionStatus === "error" ? (
          <Snackbar
            open={requestActionStatus === "error"}
            autoHideDuration={5000}
            onClose={handleClose}
            anchorOrigin={{ vertical: "top", horizontal: "center" }}
          >
            <MuiAlert
              elevation={6}
              variant="filled"
              severity="error"
              onClose={handleClose}
            >
              <Box display="flex" flexDirection="column" alignItems="center">
                <Box mb={1}>
                  {(requestActionErrorMessage as string) ??
                    t("worker-requests_error")}
                </Box>
                {/*<Button*/}
                {/*	variant="contained"*/}
                {/*	color="primary"*/}
                {/*	onClick={() => history.push("/login")}*/}
                {/*>*/}
                {/*	Try Login Again*/}
                {/*</Button>*/}
              </Box>
            </MuiAlert>
          </Snackbar>
        ) : null}
        {updateAvailabilityStatusStatus === "error" && (
          <Snackbar
            open={updateAvailabilityStatusStatus === "error"}
            autoHideDuration={2000}
            onClose={() => {
              dispatch(resetUpdateWorkerAvailabilityStatus());
              if (closeEditDialog) {
                closeEditDialog();
              }
            }}
            anchorOrigin={{ vertical: "top", horizontal: "center" }}
          >
            <Alert elevation={6} variant="filled" severity="error">
              {updateAvailabilityStatusErrorMessage}
            </Alert>
          </Snackbar>
        )}
        {updateAvailabilityStatusStatus === "fulfilled" && (
          <Snackbar
            open={updateAvailabilityStatusStatus === "fulfilled"}
            autoHideDuration={2000}
            onClose={() => {
              dispatch(resetUpdateWorkerAvailabilityStatus());
              if (fetchRequestsWithFilter) {
                fetchRequestsWithFilter();
              }
            }}
            anchorOrigin={{ vertical: "top", horizontal: "center" }}
          >
            <Alert elevation={6} variant="filled" severity="success">
              {t("worker-requests_time-off-request-updated")}
            </Alert>
          </Snackbar>
        )}
        {renotifyWorkersStatus === "fulfilled" ? (
          <Snackbar
            open={renotifyWorkersStatus === "fulfilled"}
            autoHideDuration={3000}
            onClose={() => dispatch(resetRenotifyWorkers(undefined))}
            anchorOrigin={{ vertical: "top", horizontal: "center" }}
          >
            <MuiAlert elevation={6} variant="filled" severity="success">
              {t("worker-requests_workers-notified")}
            </MuiAlert>
          </Snackbar>
        ) : null}
        {renotifyWorkersStatus === "error" ? (
          <Snackbar
            open={renotifyWorkersStatus === "error"}
            autoHideDuration={5000}
            onClose={() => dispatch(resetRenotifyWorkers(undefined))}
            anchorOrigin={{ vertical: "top", horizontal: "center" }}
          >
            <MuiAlert
              elevation={6}
              variant="filled"
              severity="error"
              onClose={handleClose}
            >
              <Box display="flex" flexDirection="column" alignItems="center">
                <Box mb={1}>
                  {t("worker-requests_notify-workers-error")}
                </Box>
              </Box>
            </MuiAlert>
          </Snackbar>
        ) : null}
        <CallDetailDrawer
          open={Boolean(callDetailRequest)}
          requestId={callDetailRequest?.requestId}
          conversationIds={callDetailRequest?.conversationIds ?? []}
          workerName={
            callDetailRequest
              ? `${callDetailRequest.worker.firstName} ${callDetailRequest.worker.lastName}`
              : ""
          }
          requestDate={callDetailRequest?.requestDate}
          onClose={() => setCallDetailRequest(null)}
        />
        <Dialog
          open={isEditCallOffOpen ?? false}
          onClose={closeEditDialog}
          maxWidth="md"
          fullWidth
        >
          {selectedRequestToEdit && (
            <CallOffSection
              workerId={selectedRequestToEdit.workerId}
              workerFullName={`${selectedRequestToEdit.worker.firstName} ${selectedRequestToEdit.worker.lastName}`}
              currentAssignment={{
                job:
                  selectedRequestToEdit.worker.primaryJobName ||
                  t("common_not-applicable"),
                workcenter: selectedRequestToEdit.worker.workCenterName,
              }}
              fromStatus={selectedRequestToEdit.status.code}
              onCancel={closeEditDialog}
              onConfirmEdit={(callOffParams) => {
                // Add the requestId to the params for the update API
                const updateParams = {
                  ...callOffParams,
                  requestId: selectedRequestToEdit.requestId,
                };
                dispatch(updateWorkerAvailabilityStatus(updateParams));
              }}
              // Pre-fill logic for Edit Mode (See next step for Component changes)
              initialData={selectedRequestToEdit}
              shiftDetails={{
                shiftPatternName: selectedRequestToEdit?.ftShift?.patternName,
                startDateTime: selectedRequestToEdit?.ftShift?.startDateTime,
                endDateTime: selectedRequestToEdit?.ftShift?.endDateTime,
              }}
            />
          )}
        </Dialog>
      </Box>
    </ConfirmProvider>
  );
};

export default FullTimeShiftsSwap;
